import {
    addDays,
    differenceInCalendarDays,
    safeFormatClientDate,
    safeFormatISODate,
    safeParseClientDate,
    safeParseISO,
    subDays,
} from 'shared/util/datetime-fns';
import type {
    BoundingBox,
    CheckboxMap,
    CheckboxTree,
    Destinations,
    FlexibleDate,
    GeoLocation,
    GeoQuery,
    Month,
    NumberRange,
} from '@/interfaces';

const isLegalMonth = (month: number, year: number): boolean => {
    if (month < 1 || month > 12) {
        return false;
    }

    const firstMonth = new Date().getMonth() + 1;
    const firstYear = new Date().getFullYear();

    if (year < firstYear || year > firstYear + 1) {
        return false;
    }
    if (month < firstMonth && year === firstYear) {
        return false;
    }
    if (month >= firstMonth && year === firstYear + 1) {
        return false;
    }

    return true;
};

const getAllParents = (location: string): string[] => {
    const result = [];
    const all = location.split(';');
    while (all.length > 1) {
        result.push(all.join(';'));
        all.pop();
    }
    return result.reverse();
};

const toggleNestedCheckbox = (tree: CheckboxTree, path: string[]): CheckboxTree => {
    const [head, ...rest] = path;
    if (rest.length <= 0) {
        const { [head]: toggledOn, ...final } = tree;
        return toggledOn ? final : { ...final, [head]: {} };
    }
    return { ...tree, [head]: toggleNestedCheckbox(tree[head] || {}, rest) };
};

export const queryToCheckboxMap = (toMap: string[] | string): CheckboxMap =>
    Array.isArray(toMap)
        ? toMap.reduce((map: CheckboxMap, item: string): CheckboxMap => {
              map[item] = true;
              return map;
          }, {})
        : { [toMap]: true };

export const queryToCheckboxTree = (toMap: string[] | string): CheckboxTree =>
    Array.isArray(toMap)
        ? toMap.reduce((hierarchy: CheckboxTree, item: string): CheckboxTree => toggleNestedCheckbox(hierarchy, getAllParents(item)), {})
        : toggleNestedCheckbox({}, getAllParents(toMap));

export const queryToString = (asString: string[] | string): string => (Array.isArray(asString) ? asString[0] : asString);

export const queryToList = (asString: string[] | string): string[] => (Array.isArray(asString) ? asString : [asString]);

const filterExtensions = (destinations: string[]): string[] =>
    destinations.filter(
        (destination) =>
            !destinations.some((other) => destination.startsWith(other) && destination !== other && destination[other.length] === ';'),
    );

export const queryToDestinations = (query: string[] | string): Destinations => ({
    list: queryToList(query),
    tree: queryToCheckboxTree(filterExtensions(queryToList(query))),
});

export const queryToNumberRange = (toRange: string[] | string): NumberRange | null => {
    const [start, end] = (Array.isArray(toRange) ? toRange[0] : toRange).split('-');
    return ~~end ? [~~start, ~~end] : null;
};

export const queryToFlexibleDate = (toRange: string[] | string): FlexibleDate => {
    const [start, end] = (Array.isArray(toRange) ? toRange[0] : toRange).split('-');
    const parsedStart = safeParseClientDate(start);

    if (end && parsedStart) {
        const parsedEnd = safeParseClientDate(end);
        if (parsedEnd) {
            const diff = differenceInCalendarDays(parsedStart, parsedEnd);

            if (diff) {
                const flexibility = diff % 2 !== 0 ? (diff + 1) / 2 : diff / 2;

                return {
                    date: safeFormatISODate(addDays(flexibility, parsedStart)) || undefined,
                    flexibility,
                };
            }
        }
    }

    return { date: safeFormatISODate(parsedStart) || undefined, flexibility: 0 };
};

export const queryToMonths = (toMonths: string[] | string): Month[] => {
    const months = Array.isArray(toMonths) ? toMonths : [toMonths];

    return months
        .map((entry: string) => {
            const [month, year] = entry.split('.');
            return isLegalMonth(~~month, ~~year) ? { month: ~~month, year: ~~year } : null;
        })
        .filter((entry: Month | null) => entry !== null) as Month[];
};

export const stringToQuery = (text: string): string => text;

export const listToQuery = (list: string[]): string[] => list;

export const checkboxMapToQuery = (map: CheckboxMap): string[] | null => {
    const values = Object.keys(map).filter((key) => map[key]);
    return values.length === 0 ? null : values;
};

// Finds every leaf node in the tree and returns them as a list
export const checkboxTreeToQuery = (map: CheckboxTree): string[] | null => {
    const values = Object.keys(map)
        .map((key) => (Object.keys(map[key]).length === 0 ? [key] : checkboxTreeToQuery(map[key])) || [])
        .reduce((acc, val) => acc.concat(val), []);
    return values.length === 0 ? null : values;
};

export const destinationsToQuery = (destinations: Destinations): string[] | null =>
    destinations.list.length === 0 ? checkboxTreeToQuery(destinations.tree) : listToQuery(destinations.list);

export const numberRangeToQuery = (range?: NumberRange): string | null => (range ? `${range[0]}-${range[1]}` : null);

export const flexibleDateToQuery = (flexDate?: FlexibleDate): string | null => {
    if (flexDate?.date) {
        const parsedDate = safeParseISO(flexDate.date);

        if (parsedDate && flexDate.flexibility) {
            const startDate = safeFormatClientDate(subDays(flexDate.flexibility, parsedDate));
            const endDate = safeFormatClientDate(addDays(flexDate.flexibility, parsedDate));

            return `${startDate}-${endDate}`;
        }
        return safeFormatClientDate(parsedDate, null);
    }
    return null;
};

export const monthsToQuery = (months: Month[]): string[] | null => (months.length === 0 ? null : months.map((e) => `${e.month}.${e.year}`));

export const queryToNumber = (numString?: string): number | undefined => {
    if (!numString) return undefined;
    const parsed = Number.parseInt(numString);
    return Number.isNaN(parsed) ? undefined : parsed;
};

export const queryToNumbers = (numString?: string | string[]): number[] | undefined => {
    const list = Array.isArray(numString) ? numString : numString?.split(',');
    const filtered = list?.map(queryToNumber).filter((it) => it !== undefined);
    return filtered?.length ? filtered : undefined;
};

export const numberToQuery = (num?: number) => (!num || Number.isNaN(num) ? undefined : num.toString());

export const numbersToQuery = (numbers?: number[]) => (numbers?.length ? numbers.join(',') : undefined);

// Geo query converters

/**
 * Converts lat, lon, radius query params to a GeoQuery object.
 * Expects params to be passed as an object with lat, lon, radius keys.
 */
export const queryToGeoQuery = (params: { lat?: string; lon?: string; radius?: string }): GeoQuery | undefined => {
    const lat = Number.parseFloat(params.lat || '');
    const lon = Number.parseFloat(params.lon || '');
    const radius = Number.parseFloat(params.radius || '');
    if (!Number.isNaN(lat) && !Number.isNaN(lon) && !Number.isNaN(radius) && radius > 0) {
        return { lat, lon, radius };
    }
    return undefined;
};

/**
 * Converts a GeoQuery object to query params.
 */
export const geoQueryToParams = (geoQuery?: GeoQuery): { lat?: string; lon?: string; radius?: string } => {
    if (geoQuery) {
        return {
            lat: geoQuery.lat.toString(),
            lon: geoQuery.lon.toString(),
            radius: geoQuery.radius.toString(),
        };
    }
    return {};
};

/**
 * Converts bbox query param to a BoundingBox object.
 * Format: minLat,minLon,maxLat,maxLon
 */
export const queryToBoundingBox = (bbox?: string | string[]): BoundingBox | undefined => {
    const bboxStr = Array.isArray(bbox) ? bbox[0] : bbox;
    if (!bboxStr) return undefined;
    const parts = bboxStr.split(',').map((p) => Number.parseFloat(p.trim()));
    if (parts.length === 4 && parts.every((p) => !Number.isNaN(p))) {
        return { minLat: parts[0], minLon: parts[1], maxLat: parts[2], maxLon: parts[3] };
    }
    return undefined;
};

/**
 * Converts a BoundingBox object to bbox query param.
 */
export const boundingBoxToQuery = (bbox?: BoundingBox): string | undefined => {
    if (bbox) {
        return `${bbox.minLat},${bbox.minLon},${bbox.maxLat},${bbox.maxLon}`;
    }
    return undefined;
};

/**
 * Converts geoLocations query param to GeoLocation array.
 * Each steder param is just a label - the combined bbox is stored separately.
 * Individual bboxes are not stored in URL to keep it short.
 */
export const queryToGeoLocations = (locations?: string | string[]): GeoLocation[] | undefined => {
    const locationArr = Array.isArray(locations) ? locations : locations ? [locations] : [];
    if (locationArr.length === 0) return undefined;

    const result: GeoLocation[] = [];
    for (const loc of locationArr) {
        const label = decodeURIComponent(loc);
        // When loaded from URL, individual bboxes are not available
        // The combined bbox from URL is used for the actual search
        result.push({
            id: label,
            label,
            boundingBox: { minLat: 0, minLon: 0, maxLat: 0, maxLon: 0 },
        });
    }

    return result.length > 0 ? result : undefined;
};

/**
 * Converts GeoLocation array to query params.
 * Just stores the labels - the combined bbox is stored separately.
 */
export const geoLocationsToQuery = (locations?: GeoLocation[]): string[] | undefined => {
    if (!locations || locations.length === 0) return undefined;
    return locations.map((loc) => loc.label);
};

/**
 * Calculate combined bounding box from multiple geo locations.
 */
export const combineBoundingBoxes = (locations: GeoLocation[]): BoundingBox | undefined => {
    if (locations.length === 0) return undefined;

    let minLat = Number.POSITIVE_INFINITY;
    let minLon = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let maxLon = Number.NEGATIVE_INFINITY;

    for (const loc of locations) {
        minLat = Math.min(minLat, loc.boundingBox.minLat);
        minLon = Math.min(minLon, loc.boundingBox.minLon);
        maxLat = Math.max(maxLat, loc.boundingBox.maxLat);
        maxLon = Math.max(maxLon, loc.boundingBox.maxLon);
    }

    return { minLat, minLon, maxLat, maxLon };
};
